import React, { useState } from 'react';
import { createStyles, makeStyles, Typography, withStyles } from '@material-ui/core';
import { useTranslation } from '../../../app/i18n';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { CustomPasswordInput, CustomTextInput } from '../../form/FormInputs';
import { MainSubmitButton, MainUnderlineButton } from 'src/components/buttons/MainButton';
import Router from 'next/router';
import { useSnackbar } from 'notistack';
import { SidebarTypes } from '../services/baseLayout.reducer';
import TermsCheckbox from 'src/components/form/TermsCheckbox';
import { useAuth } from 'src/providers/auth/AuthProvider';
import { createResponsiveFontSize } from 'src/utils/helpers/theme';
import { useBaseLayoutOpenSide } from '../BaseLayoutProvider';

const initialValues = {
    email: '',
    password: '',
    terms: false
};

const UnderlineButton = withStyles((theme) => ({
    root: { marginTop: theme.spacing(2) }
}))(MainUnderlineButton);

const useAuthStyle = makeStyles((theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            flexDirection: 'column',
            alignItems: 'center'
        },

        buttons: { marginLeft: theme.spacing(4) },
        text2: { marginTop: theme.spacing(3), marginBottom: theme.spacing(2) },
        text3: { marginTop: theme.spacing(3) },
        marginTop: { marginTop: theme.spacing(3) },
        marginBottom: { marginBottom: theme.spacing(3) },
        inputContainer: { width: '100%' },
        inputText: { ...createResponsiveFontSize(-1) },
        textButton: { color: theme.palette.primary.main },
        checkbox: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(4),
            alignItems: 'center'
        },
        checkboxLabel: {
            fontSize: theme.typography.subtitle1.fontSize,
            color: theme.palette.grey['200']
        }
    })
);
interface Props {
    mode: SidebarTypes | undefined;
}

export const AuthSidebar = ({ mode }: Props) => {
    const classes = useAuthStyle();
    const { t } = useTranslation('common');
    const [terms, toggleTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register, signIn } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const openAuthPage = useBaseLayoutOpenSide();

    const onAuthRes = (success: any) => {
        setLoading(false);
        if (!success) {
            enqueueSnackbar(t('somethingWrong'), { variant: 'error' });
        } else {
            enqueueSnackbar(t('successLogin'), { variant: 'success' });
            Router.push('/questionnaire');
        }
    };

    const isLogin = mode === 'LOGIN';

    const postLogin = async (values: typeof initialValues) => {
        setLoading(true);
        const success = await signIn(values.email, values.password);
        onAuthRes(success);
    };
    const postRegister = async (values: typeof initialValues) => {
        setLoading(true);
        const success = await register(values.email, values.password);
        onAuthRes(success);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={isLogin ? postLogin : postRegister}
            validationSchema={Yup.object({
                email: Yup.string().email().required(t('form.required')),
                password: Yup.string().required(t('form.required')),
                terms: !isLogin ? Yup.bool().oneOf([true], t('form.termsRequired')) : Yup.bool()
            })}>
            <>
                <Typography variant="h6">
                    {isLogin ? t('auth.welcomeBack') : t('auth.welcomeApsy')}
                </Typography>
                <Typography variant="caption" className={classes.text2}>
                    {isLogin ? t('auth.loginPls') : t('auth.registerExp')}
                </Typography>

                <Form autoComplete="on" className={classes.container}>
                    <CustomTextInput
                        label={t('form.email')}
                        name="email"
                        type="text"
                        theme="dark"
                        classes={{
                            root: classes.inputContainer,
                            input: classes.inputText,
                            label: classes.inputText
                        }}
                    />
                    <CustomPasswordInput
                        label={isLogin ? t('form.password') : t('form.createPassword')}
                        name="password"
                        type="password"
                        theme="dark"
                        classes={{
                            root: classes.inputContainer,
                            input: classes.inputText,
                            label: classes.inputText
                        }}
                    />

                    {isLogin ? (
                        <>
                            <Typography variant="caption" className={classes.text3}>
                                {t('auth.makeApp')}
                            </Typography>
                            <UnderlineButton
                                onClick={() => openAuthPage('REGISTER', true)}
                                textProp={{ variant: 'caption', color: 'primary' }}>
                                {t('auth.createAccount')}
                            </UnderlineButton>
                            <MainSubmitButton
                                loading={loading}
                                type="submit"
                                classes={{ root: classes.marginTop }}>
                                {t('auth.signIn')}
                            </MainSubmitButton>
                            <UnderlineButton
                                onClick={() => {}}
                                textProp={{ variant: 'caption', color: 'primary' }}>
                                {t('auth.forgotPassword')}
                            </UnderlineButton>
                        </>
                    ) : (
                        <>
                            <TermsCheckbox
                                classes={{ root: classes.checkbox }}
                                terms={terms}
                                toggleTerms={toggleTerms}
                                name="terms"
                            />
                            <MainSubmitButton
                                loading={loading}
                                type="submit"
                                classes={{ root: classes.marginTop }}>
                                {t('auth.createAccount')}
                            </MainSubmitButton>
                        </>
                    )}
                </Form>
            </>
        </Formik>
    );
};

// AuthSidebar.getStaticProps = async (context: any) => {
//     return {
//         csrfToken: await csrfToken(context),
//         namespacesRequired: ['common']
//     };
// };
